import React from 'react';

import useEffectWithoutFirst from './use-effect-without-first';
import { _ } from '../libs';

/**
 * Hook that manages local state synchronized with external metadata,
 * with debounced saves back to the external source.
 *
 * This hook handles:
 * - Syncing external changes to local state (one-way, with equality check)
 * - Debouncing local changes back to external (with empty/equality checks)
 * - Skipping the first render to avoid unnecessary saves on mount
 *
 * @template T - Type of the metadata object
 * @param externalMetadata - The external metadata value to sync with
 * @param saveMetadata - Optional function to save metadata changes (debounced)
 * @param debounceMs - Debounce delay in milliseconds (default: 1000)
 * @returns Tuple of [localMetadata, setLocalMetadata]
 *
 * @example
 * ```tsx
 * const [localUiMetadata, setLocalUiMetadata] = useDebouncedMetadata(
 *   uiMetadata,
 *   updateUiMetadata,
 *   1000
 * );
 * ```
 */
function useDebouncedMetadata<T extends Record<string, any>>(
  externalMetadata: T,
  saveMetadata?: (_metadata: T) => void | Promise<void>,
  debounceMs = 1000
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [localMetadata, setLocalMetadata] = React.useState<T>(externalMetadata);

  React.useEffect(() => {
    if (!_.isEqual(externalMetadata, localMetadata)) {
      setLocalMetadata(externalMetadata);
    }
  }, [externalMetadata]);

  const debouncedSave = React.useMemo(
    () =>
      _.debounce((metadata: T) => {
        saveMetadata?.(metadata);
      }, debounceMs),
    [saveMetadata, debounceMs]
  );

  useEffectWithoutFirst(() => {
    if (saveMetadata && !_.isEmpty(localMetadata) && !_.isEqual(localMetadata, externalMetadata)) {
      debouncedSave(localMetadata);
    }
  }, [localMetadata]);

  return [localMetadata, setLocalMetadata];
}

export default useDebouncedMetadata;
